/**
 * Nostr Office — custom event kinds and relay configuration.
 *
 * See NIP.md at the project root for the full schema documentation.
 */

/**
 * Document relays. All document events (metadata, snapshots, content
 * updates and presence) are read from and written to this fixed group of
 * well-known public relays. Because every client of this app uses the
 * same set, collaborators are guaranteed to meet on a shared relay even
 * when their personal NIP-65 relay lists do not overlap. The Nostrify
 * relay group fans out to every relay and deduplicates events, so the
 * loss of any single relay does not affect synchronization.
 */
export const DOC_RELAYS = [
  'wss://relay.ditto.pub/',
  'wss://nos.lol/',
  'wss://relay.primal.net/',
] as const;

/** Kind 38549 (addressable) — document metadata: title, collaborators, permissions. */
export const KIND_DOC_METADATA = 38549;

/** Kind 30929 (addressable) — compacted full-document snapshot (Yjs state). */
export const KIND_DOC_SNAPSHOT = 30929;

/** Kind 2701 (regular) — incremental document content update (Yjs update). */
export const KIND_DOC_UPDATE = 2701;

/** Kind 24159 (ephemeral) — live presence & remote cursors (Yjs awareness). */
export const KIND_DOC_PRESENCE = 24159;

/** Generate a new random document id (16 hex chars). */
export function generateDocId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** NIP-01 `a` tag coordinate for a document's metadata event. */
export function docCoordinate(ownerPubkey: string, docId: string): string {
  return `${KIND_DOC_METADATA}:${ownerPubkey}:${docId}`;
}

/** Parse a document coordinate string back into its parts. */
export function parseDocCoordinate(coord: string): { ownerPubkey: string; docId: string } | undefined {
  const parts = coord.split(':');
  if (parts.length !== 3) return undefined;
  const [kind, ownerPubkey, docId] = parts;
  if (kind !== String(KIND_DOC_METADATA)) return undefined;
  if (!/^[0-9a-f]{64}$/.test(ownerPubkey)) return undefined;
  if (!/^[0-9a-f]{4,64}$/.test(docId)) return undefined;
  return { ownerPubkey, docId };
}

/** NIP-31 alt tag describing a document metadata event for unknown clients. */
export function docAltTag(title: string): string[] {
  return ['alt', `Collaborative document: ${title || 'Untitled'}`];
}
